// Canvas can't resolve CSS custom properties, so read the concrete color off
// the element at draw time, with a fallback matching --primary-main.
export const FALLBACK_COLOR = "rgb(2, 136, 209)";

/**
 * The computed colour a CSS custom property yields on `el`.
 *
 * NOT `getComputedStyle(el).getPropertyValue("--x")`: that returns a custom
 * property SUBSTITUTED BUT UNCOMPUTED, so a token like
 * `light-dark(oklch(...), oklch(...))` — which is how the series palette is
 * declared — comes back verbatim. Assigning that to a canvas `fillStyle` is a
 * SILENT no-op, which drew every grouped series in Chart.js's default black
 * while the HTML legend, where the browser does compute `light-dark()`, showed
 * the right colours.
 *
 * Assigning the variable to a real property and reading it back gets the
 * computed value, which is always a resolved colour the canvas can parse. The
 * inline style is restored immediately; `color` has no effect on a canvas's
 * own painting, so nothing observable changes in between.
 */
export function computedColorOf(el: HTMLElement, variable: string): string {
  return computedColor(el, `var(${variable})`) || FALLBACK_COLOR;
}

/** The colour a CSS `<color>` expression computes to on `el`. The mechanism
 *  {@link computedColorOf} documents, shared with the hover derivation below:
 *  assign, read back the COMPUTED value, restore. */
function computedColor(el: HTMLElement, expression: string): string {
  const previous = el.style.color;
  el.style.color = expression;
  const resolved = getComputedStyle(el).color;
  el.style.color = previous;
  return resolved;
}

/** How many `--metric-series-N` slots MetricChart.module.css declares. Series
 *  past the last one cycle back to the first: the palette bounds the COLOURS,
 *  never the number of series drawn. */
const SERIES_PALETTE_SIZE = 12;

/** The CSS variable a series at `index` takes its colour from. Shared by the
 *  canvas (which resolves it against the element) and the HTML legend (which
 *  hands it to the browser verbatim), so the swatch and the line can never
 *  disagree. */
export function seriesColorVar(index: number): string {
  return `--metric-series-${String(index % SERIES_PALETTE_SIZE)}`;
}

/**
 * The colour for each partition, resolved against `canvas` because a canvas
 * cannot resolve CSS variables itself.
 *
 * A lone series keeps `--primary-main` — the chart's colour before grouping
 * existed, and the one every ungrouped card and every raw chart still draws.
 * Several series take the categorical palette instead, which is what makes
 * the legend readable.
 */
export function resolveSeriesColors(
  canvas: HTMLCanvasElement,
  count: number,
): string[] {
  const read = (name: string) => computedColorOf(canvas, name);
  if (count <= 1) {
    return [read("--primary-main")];
  }
  return Array.from({ length: count }, (_unused, i) => read(seriesColorVar(i)));
}

/**
 * How far a hovered element's colour moves toward the page's foreground, and
 * what it moves toward.
 *
 * Toward the FOREGROUND rather than simply darker: `--gray-12` is near-black
 * in light mode and near-white in dark, so a hovered bar gains contrast
 * against the ground in both themes instead of receding into it in one. 20% in
 * oklab is about the size of the step Chart.js's own default hover makes
 * (10% darker, half a step more saturated) — a clear change that still reads
 * as the same series.
 */
const HOVER_MIX_TOWARD = "--gray-12";
const HOVER_MIX_PROPORTION = "20%";

/**
 * The hovered counterpart of each resting colour in `colors`.
 *
 * Chart.js has a default for this — `hoverBackgroundColor` derives from
 * `backgroundColor` by saturating and darkening it — but it derives it with a
 * parser that predates CSS Color 4 and so cannot read the `oklch(...)` the
 * foundation's scales compute to. It returns undefined, Chart.js drops the
 * option, and the hovered bar is painted with the canvas's default fill: BLACK,
 * on a chart whose every other bar is still its series colour. Only a
 * single-series chart escaped it, because `--primary-main` happens to be an
 * sRGB literal that parser can still read.
 *
 * So the mix is done by the browser, which understands every colour space the
 * palette can be written in, and the result is read back computed — the same
 * round-trip {@link computedColorOf} makes, for the same reason. Each colour is
 * derived from ITSELF, so a repainted palette carries its hover states with it.
 */
export function resolveHoverColors(
  canvas: HTMLCanvasElement,
  colors: string[],
): string[] {
  const toward = computedColorOf(canvas, HOVER_MIX_TOWARD);
  return colors.map((color) => {
    const mixed = `color-mix(in oklab, ${color}, ${toward} ${HOVER_MIX_PROPORTION})`;
    // An expression the browser cannot parse would be dropped as an invalid
    // declaration and read back as the element's INHERITED colour — a wrong
    // colour, painted confidently, which is the failure being fixed here. A
    // series that cannot be mixed keeps its own colour instead: it loses the
    // highlight, not its identity.
    if (!CSS.supports("color", mixed)) {
      return color;
    }
    return computedColor(canvas, mixed) || color;
  });
}
